import { analytics } from '../firebase';
import { logEvent } from 'firebase/analytics';
import { bridgeGa4Event } from '../analytics/ga4Bridge';

/**
 * Analytics utility for standardized event tracking
 * 
 * Design decisions:
 * - Centralized event tracking to maintain consistency across the app
 * - Structured event naming using snake_case for events and camelCase for params
 * - Error handling to prevent analytics issues from affecting user experience
 * - Performance optimized with debouncing for high-frequency events
 * - Future-proof structure for easy extension of tracking capabilities
 */

// Constants for event names to ensure consistency
export const ANALYTICS_EVENTS = {
  // Assessment module events
  MODULE_CARD_CLICKED: 'assessment_module_card_clicked',
  MODULE_COMPLETED: 'assessment_module_completed',
  MODULE_ABANDONED: 'assessment_module_abandoned',
  SECTION_COMPLETED: 'assessment_section_completed',
  MODULE_SAVED: 'assessment_module_saved',
  
  // Page view events
  PAGE_VIEW: 'page_view',
  PATIENT_OVERVIEW_VIEWED: 'patient_overview_viewed',
  PRE_ASSESSMENT_DATA_VIEWED: 'pre_assessment_data_viewed',
  
  // Document events
  DOCUMENTS_ACCESSED: 'documents_accessed',
  
  // Diagnostic flow events
  DIAGNOSIS_FLOW_DETERMINED: 'diagnosis_flow_determined',
  
  // Report bundle events
  REPORT_BUNDLE_DIALOG_OPENED: 'report_bundle_dialog_opened',
  REPORT_BUNDLE_DOWNLOAD_STARTED: 'report_bundle_download_started',
  REPORT_BUNDLE_DOWNLOAD_SUCCEEDED: 'report_bundle_download_succeeded',
  REPORT_BUNDLE_DOWNLOAD_FAILED: 'report_bundle_download_failed',

  // Staff management — edit staff email
  EDIT_STAFF_EMAIL_OPENED: 'edit_staff_email_opened',
  EDIT_STAFF_EMAIL_SUCCESS: 'edit_staff_email_success',
  EDIT_STAFF_EMAIL_FAILED: 'edit_staff_email_failed',
  EDIT_STAFF_EMAIL_DUPLICATE_BLOCKED: 'edit_staff_email_duplicate_blocked',
  EDIT_STAFF_EMAIL_CANCELLED: 'edit_staff_email_cancelled',
  RESEND_STAFF_WELCOME_CLICKED: 'resend_staff_welcome_clicked',
  RESEND_STAFF_WELCOME_SUCCESS: 'resend_staff_welcome_success',
  RESEND_STAFF_WELCOME_FAILED: 'resend_staff_welcome_failed',

  // Staff management — disable / enable account (account lifecycle)
  DISABLE_STAFF_ACCOUNT_OPENED: 'disable_staff_account_opened',
  DISABLE_STAFF_ACCOUNT_SUCCESS: 'disable_staff_account_success',
  DISABLE_STAFF_ACCOUNT_FAILED: 'disable_staff_account_failed',
  ENABLE_STAFF_ACCOUNT_OPENED: 'enable_staff_account_opened',
  ENABLE_STAFF_ACCOUNT_SUCCESS: 'enable_staff_account_success',
  ENABLE_STAFF_ACCOUNT_FAILED: 'enable_staff_account_failed',

  // Staff management — change account type
  CHANGE_STAFF_ACCOUNT_TYPE_OPENED: 'change_staff_account_type_opened',
  CHANGE_STAFF_ACCOUNT_TYPE_SUCCESS: 'change_staff_account_type_success',
  CHANGE_STAFF_ACCOUNT_TYPE_FAILED: 'change_staff_account_type_failed',
  // Fired when the change is blocked by a guard rail (assigned patients on a
  // clinical->non-clinical move, or the last-admin protection).
  CHANGE_STAFF_ACCOUNT_TYPE_BLOCKED: 'change_staff_account_type_blocked',
};

// Assessment module types
export const MODULE_TYPES = {
  CLINICAL_INTERVIEW: 'clinical_interview',
  SEMI_STRUCTURED_INTERVIEW: 'semi_structured_interview',
  DIAGNOSIS_DECISION: 'diagnosis_decision',
  POST_ASSESSMENT_DISCUSSION: 'post_assessment_discussion',
};

/**
 * Track events with standardized structure and error handling
 * @param {string} eventName - Name of the event to track
 * @param {Object} eventParams - Parameters to include with the event
 */
export const trackEvent = (eventName, eventParams = {}) => {
  try {
    // Add common parameters to all events
    const enrichedParams = {
      ...eventParams,
      timestamp: new Date().toISOString(),
      // Add any additional global context here (e.g., app version)
    };

    // Log to console in development for debugging
    if (process.env.NODE_ENV === 'development') {
      console.log(`📊 Analytics Event: ${eventName}`, enrichedParams);
    }

    // Send to Firebase Analytics
    logEvent(analytics, eventName, enrichedParams);
  } catch (error) {
    // Silently fail to prevent app disruption, but log in development
    if (process.env.NODE_ENV === 'development') {
      console.error('Analytics error:', error);
    }
  }
  // Dual-write into the first-party BigQuery stream (src/analytics/ga4Bridge.js).
  // Outside the try above on purpose: a GA4 failure must not take the
  // first-party row with it, and the bridge swallows its own errors.
  bridgeGa4Event(eventName, eventParams);
};

/**
 * Track when a clinician clicks on an assessment module card
 * @param {string} moduleType - Type of assessment module
 * @param {string} patientId - Anonymized patient identifier
 * @param {string} userId - Anonymized user identifier (typically the clinician)
 * @param {Object} additionalParams - Any additional tracking parameters
 */
export const trackAssessmentModuleCardClicked = (
  moduleType,
  patientId,
  userId,
  additionalParams = {}
) => {
  trackEvent(ANALYTICS_EVENTS.MODULE_CARD_CLICKED, {
    moduleType,
    patientId,
    userId,
    ...additionalParams,
  });
};

/**
 * Track when a clinician completes a section within an assessment module
 * @param {string} moduleType - Type of assessment module
 * @param {string} sectionId - Identifier for the section completed
 * @param {string} patientId - Anonymized patient identifier
 * @param {string} userId - Anonymized user identifier (typically the clinician)
 * @param {Object} additionalParams - Additional tracking parameters like timeSpent
 */
export const trackSectionCompleted = (
  moduleType,
  sectionId,
  patientId,
  userId,
  additionalParams = {}
) => {
  trackEvent(ANALYTICS_EVENTS.SECTION_COMPLETED, {
    moduleType,
    sectionId,
    patientId,
    userId,
    completedAt: new Date().toISOString(),
    ...additionalParams,
  });
};

/**
 * Track when a clinician completes an entire assessment module
 * @param {string} moduleType - Type of assessment module
 * @param {string} patientId - Anonymized patient identifier
 * @param {string} userId - Anonymized user identifier (typically the clinician)
 * @param {number} timeSpentMs - Time spent on the module in milliseconds
 * @param {Object} additionalParams - Any additional tracking parameters
 */
export const trackAssessmentModuleCompleted = (
  moduleType,
  patientId,
  userId,
  timeSpentMs,
  additionalParams = {}
) => {
  trackEvent(ANALYTICS_EVENTS.MODULE_COMPLETED, {
    moduleType,
    patientId,
    userId,
    timeSpentMs,
    completedAt: new Date().toISOString(),
    ...additionalParams,
  });
};

// Create a named object for the default export
const analyticsService = {
  trackEvent,
  trackAssessmentModuleCardClicked,
  trackSectionCompleted,
  trackAssessmentModuleCompleted,
  ANALYTICS_EVENTS,
  MODULE_TYPES,
};

export default analyticsService; 